import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, frames, useMediaQuery } from "../../../Styles";
import { FilterPopup, NewSelect, PageHeader, NewButton, TableLayout } from "../../../Components";
import { NewFieldStyles, ListData_ValueTypes, TableStyles_Types } from "../../../Definitions";
import { useAppSelector } from "../../../Store";
import { useLazyQuery } from "@apollo/client";
import { AnimalInfo, Graph_QL, useAnimal, useLanControllers } from "../../../Services";
import { CONTROLLER_LOG_AICO, LanControllerLogAicoParameters, LanControllerLogAicoResponse } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/controllerlogaico";
import { Pages } from "../../../Navigation/root";
import { ColumnFiltersState, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../../../Components/Tables/reactiveTableRenderer";
import { useColumnConstructor } from "../../../Components/Tables/_Tables/useColumnConstructor";

// #region | TransformMessage
const transformMessage = (msg: string) => {
  return msg
    .replace(/\bF(\d+)/g, "feedcurve $1") // Replace F<number> with feedcurve<number>
    .replace(/\bM(\d+)/g, "Milestone $1") // Optional: Replace M<number> with Milestone<number>
    .replace(/\bD(\d+)/g, "Day $1"); // Optional: Replace D<number> with Day<number>
};
// #endregion

export default function Logbook() {
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: 650px)`);
  
  // #region | GraphQL
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const { call: getLanControllers, data: lanControllerData } = useLanControllers(connection);
  const { call: getAnimalsInfo, data: animalsState } = useAnimal(connection);
  const [fetchLogs, { data: logResponse, loading, error, refetch}] = useLazyQuery<LanControllerLogAicoResponse, LanControllerLogAicoParameters>(CONTROLLER_LOG_AICO, { fetchPolicy: "network-only", notifyOnNetworkStatusChange: true});
  
  const animals = useMemo(() => { return animalsState?.data }, [animalsState]);
  const logData = useMemo(() => { return logResponse?.controller.controllerLogsAico ?? [] }, [logResponse]);

  useEffect(() => {
    getLanControllers({ fetchAmount: 500 });
  }, []);
  // #endregion

  // Lan controller options and select state
  const [select, setSelect] = useState<number | undefined>(-1);
  
  const lanControllerOptions = useMemo(() => {
    if (lanControllerData && lanControllerData[domain]) {
      return [
        { label: t("logPages.logbook.allControllers", {defaultValue: "all controllers"}), id: -1 },
        ...lanControllerData[domain].data?.map(lanController => ({
          label: lanController.name ?? "",
          id: lanController.lan_controller_id,
        })),
      ];
    }
    return [];
  }, [domain, lanControllerData, t]);

  // #region | Definitions
  const typeMap: Record<number, string> = {
    1: t("logPages.logbook.system", {defaultValue: "System"}),
    2: t("logPages.logbook.motor", {defaultValue: "Motor"}),
    3: t("logPages.logbook.animal", {defaultValue: "Animal"}),
  };

  function getTypeOptions() {
    return Object.entries(typeMap).map(([key, value]) => ({ label: value, id: +key }));
  }

  const levelMap: Record<number, string> = {
    0: t("logPages.logbook.problem", {defaultValue: "Problem"}),
    1: t("logPages.logbook.warning", {defaultValue: "Warning"}),
    2: t("logPages.logbook.info", {defaultValue: "Info"}),
    3: t("logPages.logbook.debug", {defaultValue: "Debug"}),
  };

  function getLevelOptions() {
    return Object.entries(levelMap).map(([key, value]) => ({ label: value, id: +key }));
  }
  // #endregion

  // Map of animal_id to AnimalInfo for quick lookup
  const animalMapFromRedux = useMemo(() => {
    const map: Record<number, AnimalInfo> = {};
    animals?.data?.forEach(animal => {
      map[animal.animal_id] = animal;
    });
    return map;
  }, [animals]);

  useEffect(() => {
    if (!logData || logData.length === 0) return;

    const animalIds = Array.from(
      new Set(logData.map(log => log.fk_animals).filter(Boolean))
    );

    const missingIds = animalIds.filter(id => !animalMapFromRedux[id]);

    if (missingIds.length > 0) {
      getAnimalsInfo({ ids: missingIds });
    }
  }, [logData, animalMapFromRedux, getAnimalsInfo]);

  // Prepare all IDs for "all" selection
  const allIds: number[] = lanControllerOptions
    .filter(opt => opt.id !== -1 && opt.id !== undefined)
    .map(opt => Number(opt.id));

  // Input for fetching logs
  const input = {
    fetchAmount: 501,
    offset: 0,
    ids: select === -1 ? allIds : [select as number],
  };
    
  // Fetch logs button
  const getLogHandler = () => {
    if (!select) return;
    table.firstPage();
    fetchLogs({
      variables: {
        domain,
        input,
        filter: {},
      },
    });
  };

  // #region | Table setup
  const accessorData = [
    { headerValue: t("logPages.logbook.time", {defaultValue: "time"}), accessor: "timestamp", type: ListData_ValueTypes.Date, sort: true, width: "200px" },
    { headerValue: t("logPages.logbook.level", {defaultValue: "level"}), accessor: "level", type: ListData_ValueTypes.List, sort: true, width: "120px", filter: true, options: getLevelOptions() },
    { headerValue: t("logPages.logbook.type", {defaultValue: "type"}), accessor: "type", type: ListData_ValueTypes.List, sort: true, width: "120px", filter: true, options: getTypeOptions() },
    { headerValue: t("logPages.logbook.message", {defaultValue: "message"}), accessor: "message", type: ListData_ValueTypes.String, sort: true},
    { headerValue: t("logPages.logbook.location", {defaultValue: "location"}), accessor: "location", type: ListData_ValueTypes.String, sort: true, width: "150px" },
    { headerValue: t("logPages.logbook.animal", {defaultValue: "animal"}), accessor: "animalid", type: ListData_ValueTypes.String, sort: true, width: "150px" },
    { headerValue: t("logPages.logbook.controller", {defaultValue: "controller"}), accessor: "controllername", type: ListData_ValueTypes.String, sort: true, width: "250px" },
  ]

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const pageSize = 500;
  const totalLogs = logResponse?.controller?.controllerLogsAico.length ?? 0;
  const totalPages = Math.ceil(totalLogs / pageSize);

  const tableData = useMemo(() => {
    return logData.map((log) => {
      const animalInfo = log.fk_animals ? animalMapFromRedux[log.fk_animals] : null;

      return {
        id: log.lan_controller_log_id,
        timestamp: new Date(log.timestamp * 1000),
        level: { label: levelMap[log.level], id: log.level },
        type: { label: typeMap[log.type], id: log.type },
        message: transformMessage(log.message),
        location: log.location_name ?? "",
        animalid: ( <>
          {animalInfo?.animal_number && `${t("logPages.logbook.animal", {defaultValue: "animal"})}: ${animalInfo.animal_number}`}{" "}&nbsp;&nbsp; {animalInfo?.location_id && `${t("logPages.logbook.location", {defaultValue: "location"})}: ${animalInfo.location_id}`}
          <br />
          {animalInfo?.responder_number && `${t("logPages.logbook.responder", {defaultValue: "responder"})}: ${animalInfo.responder_number}`}
        </> ),
        controllername: (
          <>
            {log.controller_name && `${t("logPages.logbook.controllerName", {defaultValue: "controller name"})}: ${log.controller_name}`} 
            <br />
            {log.device_name && `${t("logPages.logbook.deviceName", {defaultValue: "device name"})}: ${log.device_name}`}
          </>
        ),
      };
    });
  }, [logData, animalMapFromRedux]);

  const columns = useColumnConstructor({
    accessorData,
    tableStyle: TableStyles_Types.primaryTable,
    canEdit: false,
  });

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    manualPagination: true,
    pageCount: totalPages, 
    state: { columnFilters },
    initialState: {
      sorting: [{ id: "timestamp", desc: true }],
      pagination: { pageSize },
    }
  });
  // #endregion

  return (
    <frames.Page_inner>
      <PageHeader
        topHeader={t(`menuHeaders.${Pages.logbook}`, { defaultValue: "logbook" })}
        subHeader={t("menuHeaders.overview", { defaultValue: "overview" })}
      />
      <frames._page_inner_content>
        <TableLayout
          searchbBarArea={
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
               <NewSelect
                onChange={(value) => setSelect(Number(value?.id))}
                options={lanControllerOptions}
                placeholder={t("logPages.logbook.lanControllers", {defaultValue: "lan controllers"})}
                fieldOverrides={{
                  fieldStyle: NewFieldStyles.greySecondary,
                  fieldWidth: mobileXs ? "150px" : undefined
                }}
                valueOverrides={{
                  defaultValue: lanControllerOptions[0]
                }}
              />
              <NewButton
                content={t("logPages.logbook.getLog", { defaultValue: "get log" })}
                 overrides={{
                    baseStyles: { width: "fit-content", backgroundColor: colors.greySecondary, height: "30px", padding: "0 10px" },
                    contentStyles: { color: colors.greyLightTertiary, textShadow: "none", ...fonts.fontSize4 },
                    hoverStyles: { backgroundColor: colors.greyPrimary }
                  }}
                onClick={getLogHandler}
              />
            </div>
          }
          buttonArea={<FilterPopup accessorData={accessorData} table={table} />}
          tableArea={ReactiveTable({ table, minRows: 30, tableType: TableStyles_Types.primaryTable, emptyText: t("logPages.logbook.noLogsAvailable", { defaultValue: "no logs available" }), renderFooter: false, loading })}
          paginationBtns={{
            onPrev: () => { if (!select) return; if (table.getCanPreviousPage()) { refetch({ input: { ...input, offset: 500 * (table.getState().pagination.pageIndex - 1)}}); table.previousPage() }},
            onNext: () => { if (!select) return; if (table.getCanNextPage() && (logData?.length && logData.length > 500)) { refetch({ input: { ...input, offset: 500 * (table.getState().pagination.pageIndex + 1) } }); table.nextPage() } },
            canNextPage: !table.getCanNextPage(),
            canPreviousPage: !table.getCanPreviousPage(),
            thisPageIndex: table.getState().pagination.pageIndex + 1,
            pageCount: table.getPageCount()
          }}
        />
      </frames._page_inner_content>
    </frames.Page_inner>
  );
}
